import React from 'react';
import { Card } from '@/components/ui/card';
import { CalendarRange, ChevronRight, AlertTriangle } from 'lucide-react';
import type { ScenarioSummary } from '@/services/strategyService';
import {
  parseScenarioName, fmtMoney, pnlColor, fmtPF, fmtShortDate, fmtRelative,
} from './tradeViewerUtils';

interface Props {
  scenario: ScenarioSummary;
  onOpen: (id: string) => void;
}

/** Small label/value pair in the card's metric row. */
const Metric: React.FC<{ label: string; value: React.ReactNode; className?: string }> = ({ label, value, className }) => (
  <div className="min-w-0">
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground truncate">{label}</div>
    <div className={`text-sm font-semibold tabular-nums truncate ${className ?? ''}`}>{value}</div>
  </div>
);

const statusTone: Record<string, string> = {
  Completed: 'bg-emerald-500/15 text-emerald-500',
  Running: 'bg-blue-500/15 text-blue-400',
  Pending: 'bg-muted text-muted-foreground',
  Failed: 'bg-red-500/15 text-red-500',
  Stopped: 'bg-amber-500/15 text-amber-500',
};

export const ScenarioCard: React.FC<Props> = ({ scenario, onOpen }) => {
  const { run, entry, mgmt } = parseScenarioName(scenario.name);
  const st = scenario.statistics;
  // `??` won't fall through a real 0, and Marten deserializes the non-nullable
  // TradeCount as 0 for run documents written before it was snapshotted — those
  // have their results in statistics only, and rendered as "no trades".
  const trades = scenario.tradeCount || st?.totalTrades || 0;
  const winRate = st?.winRate ?? 0;
  const winners = st?.winners ?? 0;
  const losers = st?.losers ?? 0;
  const hasResults = st != null && trades > 0;

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={() => onOpen(scenario.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(scenario.id); } }}
      className="group relative flex flex-col p-4 cursor-pointer transition-all
                 hover:border-primary/50 hover:shadow-md hover:-translate-y-0.5
                 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {/* Title block — entry strategy is the headline, run/mgmt are badges */}
      <div className="flex items-start gap-2 mb-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold leading-snug line-clamp-2" title={scenario.name}>{entry}</h3>
          <div className="flex flex-wrap items-center gap-1 mt-1.5">
            {run && (
              <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold bg-primary/10 text-primary">{run}</span>
            )}
            {mgmt && (
              <span className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground truncate max-w-[160px]" title={mgmt}>
                {mgmt}
              </span>
            )}
            {scenario.status !== 'Completed' && (
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${statusTone[scenario.status] ?? statusTone.Pending}`}>
                {scenario.status}
              </span>
            )}
          </div>
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/40 transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
      </div>

      {hasResults ? (
        <>
          {/* Headline P&L */}
          <div className={`text-2xl font-bold tabular-nums leading-none ${pnlColor(st!.totalPnL)}`}>
            {fmtMoney(st!.totalPnL)}
          </div>

          {/* Win-rate bar — emerald winners against red losers */}
          <div className="mt-2.5 mb-3">
            <div className="flex items-baseline justify-between text-[11px] mb-1">
              <span className="font-semibold tabular-nums">{winRate.toFixed(1)}% win</span>
              <span className="text-muted-foreground tabular-nums">{winners}W / {losers}L</span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-red-500/25 overflow-hidden">
              <div
                className="h-full rounded-full bg-emerald-500 transition-[width]"
                style={{ width: `${Math.max(0, Math.min(100, winRate))}%` }}
              />
            </div>
          </div>

          {/* Metric row */}
          <div className="grid grid-cols-4 gap-2 pt-3 border-t">
            <Metric label="Trades" value={trades} />
            <Metric label="PF" value={fmtPF(st!.profitFactor)}
              className={st!.profitFactor >= 1 ? 'text-emerald-500' : 'text-red-500'} />
            <Metric label="Avg R" value={st!.avgRMultiple.toFixed(2)} className={pnlColor(st!.avgRMultiple)} />
            <Metric label="Max DD" value={`${st!.maxDrawdownPercent.toFixed(1)}%`} className="text-red-500" />
          </div>
        </>
      ) : (
        <div className="flex-1 flex items-center gap-2 py-4 text-xs text-muted-foreground">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
          {scenario.error
            ? <span className="line-clamp-2" title={scenario.error}>{scenario.error}</span>
            : <span>No trades recorded for this run.</span>}
        </div>
      )}

      {/* Footer — backtest window + age */}
      <div className="flex items-center gap-1.5 mt-3 pt-2.5 border-t text-[11px] text-muted-foreground">
        <CalendarRange className="h-3 w-3 shrink-0" />
        {scenario.backtest
          ? <span className="tabular-nums">{fmtShortDate(scenario.backtest.startDate)} → {fmtShortDate(scenario.backtest.endDate)}</span>
          : <span>{scenario.mode}</span>}
        <span className="ml-auto shrink-0">{fmtRelative(scenario.createdAt)}</span>
      </div>
    </Card>
  );
};

export default ScenarioCard;
